<template>
  <div class="border-t border-growth-50 px-4 py-3">
    <div v-if="!shouldRender" class="text-center text-xs text-growth-400 py-6">
      No tag data available
    </div>
    <template v-else>
      <div class="flex items-baseline justify-between mb-3">
        <div>
          <div class="text-sm font-semibold text-growth-800">Tag Coverage</div>
          <div class="text-[11px] text-growth-500 mt-0.5">{{ effectiveSubtitle }}</div>
        </div>
        <div class="text-[11px] text-growth-500 tabular-nums">
          {{ effectiveCenterCount }} of {{ totalCount }}
          <span class="text-growth-400">({{ effectivePercent }}%)</span>
        </div>
      </div>
      <div class="flex gap-6 items-stretch">
        <!-- Donut + legend (legend on the right of donut) -->
        <div class="flex items-center gap-4 shrink-0 pr-6 border-r border-gray-200">
          <svg :viewBox="`0 0 ${size} ${size}`" :width="size" :height="size" class="block shrink-0">
            <g :transform="`translate(${size/2}, ${size/2})`">
              <template v-if="effectiveDonutSlices.length > 0">
                <path
                  v-for="(slice, i) in effectiveDonutSlices"
                  :key="i"
                  :d="slice.path"
                  :fill="slice.color"
                  class="cursor-default transition-opacity duration-150"
                  :opacity="hoveredSlice !== null && hoveredSlice !== i ? 0.5 : 1"
                  @mouseenter="hoveredSlice = i"
                  @mouseleave="hoveredSlice = null"
                >
                  <title>{{ slice.label }}: {{ slice.count }} ({{ slice.percent }}%)</title>
                </path>
              </template>
              <template v-else>
                <circle :r="(outerRadius + innerRadius) / 2" fill="none" stroke="#e5e7eb" :stroke-width="outerRadius - innerRadius" />
              </template>
            </g>
            <text :x="size/2" :y="size/2 - 6" text-anchor="middle" dominant-baseline="auto"
              class="fill-growth-800" style="font-size: 18px; font-weight: 600;">
              {{ effectiveCenterCount }}
            </text>
            <text :x="size/2" :y="size/2 + 10" text-anchor="middle" dominant-baseline="auto"
              class="fill-growth-400" style="font-size: 10px;">
              of {{ totalCount }}
            </text>
          </svg>
          <div class="flex flex-col gap-1.5">
            <div
              v-for="(slice, i) in effectiveLegendSlices"
              :key="i"
              class="flex items-center gap-2 cursor-default"
              @mouseenter="hoveredSlice = slice.donutIndex"
              @mouseleave="hoveredSlice = null"
            >
              <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ backgroundColor: slice.color, opacity: 0.85 }"></span>
              <span class="text-[11px] text-growth-700 whitespace-nowrap">
                {{ slice.label }}
                <span class="text-growth-400 ml-1 tabular-nums">({{ slice.count }})</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Bars -->
        <div class="flex-1 flex flex-col gap-2 justify-center min-w-0 py-1">
          <div v-for="bar in effectiveBars" :key="bar.id" class="flex items-center gap-3">
            <div class="sensitive text-xs font-medium text-growth-800 w-44 truncate shrink-0" :title="bar.label">
              {{ bar.label }}
            </div>
            <div class="flex-1 bg-gray-100 rounded h-4 overflow-hidden min-w-0">
              <div
                class="h-4 rounded-l transition-all duration-500 ease-out"
                :style="{ width: Math.max(bar.percent, 2) + '%', backgroundColor: bar.color, opacity: 0.75 }"
              />
            </div>
            <div class="text-[11px] text-growth-600 w-28 text-right shrink-0 tabular-nums">
              {{ bar.percent }}%
              <span class="text-growth-400">({{ bar.count }}/{{ bar.total }})</span>
            </div>
          </div>
          <div v-if="effectiveBars.length === 0" class="text-xs text-growth-400 py-4 text-center">
            No tag data available
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'EntityTagCoverage',
  props: {
    allEntities: { type: Array, required: true },
    tagGroups: { type: Array, default: () => [] },
    groupsMode: { type: String, default: 'and' },
    infoTagLookup: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      hoveredSlice: null,
      size: 150,
      outerRadius: 68,
      innerRadius: 42
    }
  },
  computed: {
    activeGroups() {
      return (this.tagGroups || []).filter(g =>
        (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
      )
    },

    hasAnyActiveTags() {
      return this.activeGroups.length > 0
    },

    includeTagIds() {
      return [...new Set(this.activeGroups.flatMap(g => g.include || []))]
    },

    totalCount() {
      return this.allEntities.length
    },

    matchedEntities() {
      return this.allEntities.filter(e => this.entityMatches(e))
    },

    matchedCount() {
      return this.matchedEntities.length
    },

    taggedCount() {
      return this.allEntities.filter(e => (e.tags || []).length > 0).length
    },

    filterSummary() {
      const groups = this.activeGroups
      if (groups.length === 0) return ''
      const groupStrs = groups.map(g => {
        const include = g.include || []
        const exclude = g.exclude || []
        const mode = g.mode === 'and' ? 'and' : 'or'
        const incNames = include.map(id => this.infoTagLookup[id]?.name || 'Tag')
        const excNames = exclude.map(id => this.infoTagLookup[id]?.name || 'Tag')
        let s = incNames.join(mode === 'and' ? ' AND ' : ' OR ')
        if (excNames.length) {
          const notPart = excNames.map(n => `NOT ${n}`).join(' AND ')
          s = s ? `${s} AND ${notPart}` : notPart
        }
        return s
      })
      if (groupStrs.length === 1) return groupStrs[0]
      const joiner = this.groupsMode === 'and' ? ' AND ' : ' OR '
      return groupStrs.map(s => `(${s})`).join(joiner)
    },

    rawSlices() {
      const total = this.totalCount
      if (total === 0) return []

      const includeSet = new Set(this.includeTagIds)
      const signatureCounts = {}
      for (const e of this.matchedEntities) {
        const ids = (e.tags || [])
          .map(t => t.id)
          .filter(id => includeSet.has(id))
        const key = ids.length === 0 ? '__empty__' : [...ids].sort((a, b) => a - b).join(',')
        signatureCounts[key] = (signatureCounts[key] || 0) + 1
      }

      const comboPalette = ['#a78bfa', '#60a5fa', '#fb7185', '#34d399', '#fbbf24', '#f472b6', '#fb923c', '#22d3ee']
      const matchedNoIncludeColor = '#10b981'
      const comboKeys = Object.entries(signatureCounts)
        .filter(([k]) => k !== '__empty__' && k.includes(','))
        .sort((a, b) => b[1] - a[1])
        .map(([k]) => k)
      const comboColorByKey = {}
      comboKeys.forEach((k, i) => {
        comboColorByKey[k] = comboPalette[i % comboPalette.length]
      })

      const slices = []
      for (const [key, count] of Object.entries(signatureCounts)) {
        if (key === '__empty__') {
          slices.push({ label: 'Matched', count, color: matchedNoIncludeColor })
          continue
        }
        const ids = key.split(',').map(Number)
        if (ids.length === 1) {
          const tag = this.infoTagLookup[ids[0]]
          slices.push({
            label: tag?.name || 'Tag',
            count,
            color: tag?.color || comboPalette[0]
          })
        } else {
          const names = ids.map(id => this.infoTagLookup[id]?.name || 'Tag').join(' + ')
          slices.push({ label: names, count, color: comboColorByKey[key] })
        }
      }

      slices.sort((a, b) => {
        const aCombo = a.label.includes(' + ') ? 1 : 0
        const bCombo = b.label.includes(' + ') ? 1 : 0
        if (aCombo !== bCombo) return aCombo - bCombo
        return b.count - a.count
      })

      const unmatched = total - this.matchedCount
      if (unmatched > 0) {
        slices.push({ label: "Doesn't match", count: unmatched, color: '#e5e7eb' })
      }

      return slices
    },

    allSlicesForLegend() {
      let donutIdx = 0
      return this.rawSlices.map(s => {
        const inDonut = s.count > 0
        const obj = { ...s, donutIndex: inDonut ? donutIdx : null }
        if (inDonut) donutIdx++
        return obj
      })
    },

    donutSlices() {
      const nonZero = this.rawSlices.filter(s => s.count > 0)
      const total = nonZero.reduce((sum, s) => sum + s.count, 0)
      if (total === 0) return []

      let cumulative = 0
      return nonZero.map(s => {
        const fraction = s.count / total
        const startAngle = cumulative
        const endAngle = cumulative + fraction
        cumulative = endAngle
        return {
          ...s,
          percent: Math.round((s.count / this.totalCount) * 100),
          path: this.arcPath(startAngle, endAngle)
        }
      })
    },

    tagBars() {
      return this.includeTagIds.map(tid => {
        const tag = this.infoTagLookup[tid]
        const count = this.allEntities.filter(e =>
          (e.tags || []).some(t => t.id === tid)
        ).length
        const total = this.totalCount
        return {
          id: tid,
          label: tag?.name || `Tag ${tid}`,
          count,
          total,
          percent: total > 0 ? Math.round((count / total) * 100) : 0,
          color: tag?.color || '#10b981'
        }
      })
    },

    // Default-mode (no active tag scope) outputs.
    summaryDonutSlices() {
      const total = this.totalCount
      if (total === 0) return []
      const tagged = this.taggedCount
      const untagged = total - tagged
      const items = [
        { label: 'Tagged', count: tagged, color: '#4C6E97' },
        { label: 'Untagged', count: untagged, color: '#d1d5db' },
      ].filter(s => s.count > 0)
      const sum = items.reduce((s, x) => s + x.count, 0)
      if (sum === 0) return []
      let cumulative = 0
      return items.map(s => {
        const fraction = s.count / sum
        const start = cumulative
        const end = cumulative + fraction
        cumulative = end
        return {
          ...s,
          percent: Math.round((s.count / total) * 100),
          path: this.arcPath(start, end),
        }
      })
    },

    summaryBars() {
      const total = this.totalCount
      if (total === 0) return []
      const byType = {}
      for (const e of this.allEntities) {
        const types = new Set((e.tags || []).map(t => t.type).filter(Boolean))
        for (const t of types) byType[t] = (byType[t] || 0) + 1
      }
      return Object.entries(byType)
        .sort((a, b) => b[1] - a[1])
        .map(([type, count]) => ({
          id: 'type:' + type,
          label: type,
          count,
          total,
          percent: total > 0 ? Math.round((count / total) * 100) : 0,
          color: '#4C6E97',
        }))
    },

    effectiveDonutSlices() {
      return this.hasAnyActiveTags ? this.donutSlices : this.summaryDonutSlices
    },

    effectiveBars() {
      return this.hasAnyActiveTags ? this.tagBars : this.summaryBars
    },

    effectiveCenterCount() {
      return this.hasAnyActiveTags ? this.matchedCount : this.taggedCount
    },

    effectivePercent() {
      if (this.totalCount === 0) return 0
      return Math.round((this.effectiveCenterCount / this.totalCount) * 100)
    },

    effectiveSubtitle() {
      return this.hasAnyActiveTags ? this.filterSummary : 'All tag types'
    },

    effectiveLegendSlices() {
      if (this.hasAnyActiveTags) return this.allSlicesForLegend
      let idx = 0
      return this.summaryDonutSlices.map(s => {
        const obj = { ...s, donutIndex: idx }
        idx++
        return obj
      })
    },

    shouldRender() {
      if (this.totalCount === 0) return false
      return this.hasAnyActiveTags || this.taggedCount > 0
    },
  },
  methods: {
    entityMatches(entity) {
      if (this.activeGroups.length === 0) return true
      const tagIds = new Set((entity.tags || []).map(t => t.id))
      const groupResults = this.activeGroups.map(g => {
        const include = g.include || []
        const exclude = g.exclude || []
        const mode = g.mode === 'and' ? 'and' : 'or'
        const incOk = include.length === 0
          ? true
          : (mode === 'and' ? include.every(tid => tagIds.has(tid)) : include.some(tid => tagIds.has(tid)))
        const excOk = !exclude.some(tid => tagIds.has(tid))
        return incOk && excOk
      })
      return this.groupsMode === 'and' ? groupResults.every(Boolean) : groupResults.some(Boolean)
    },

    arcPath(startFraction, endFraction) {
      if (endFraction - startFraction >= 0.9999) {
        return this.fullCirclePath()
      }

      const gap = 0.003
      const s = (startFraction + gap) * 2 * Math.PI - Math.PI / 2
      const e = (endFraction - gap) * 2 * Math.PI - Math.PI / 2
      if (e <= s) return ''

      const largeArc = (endFraction - startFraction) > 0.5 ? 1 : 0
      const oR = this.outerRadius
      const iR = this.innerRadius

      const x1 = oR * Math.cos(s)
      const y1 = oR * Math.sin(s)
      const x2 = oR * Math.cos(e)
      const y2 = oR * Math.sin(e)
      const x3 = iR * Math.cos(e)
      const y3 = iR * Math.sin(e)
      const x4 = iR * Math.cos(s)
      const y4 = iR * Math.sin(s)

      return [
        `M ${x1} ${y1}`,
        `A ${oR} ${oR} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${iR} ${iR} 0 ${largeArc} 0 ${x4} ${y4}`,
        'Z'
      ].join(' ')
    },

    fullCirclePath() {
      const oR = this.outerRadius
      const iR = this.innerRadius
      return [
        `M 0 ${-oR}`,
        `A ${oR} ${oR} 0 1 1 0 ${oR}`,
        `A ${oR} ${oR} 0 1 1 0 ${-oR}`,
        'Z',
        `M 0 ${-iR}`,
        `A ${iR} ${iR} 0 1 0 0 ${iR}`,
        `A ${iR} ${iR} 0 1 0 0 ${-iR}`,
        'Z'
      ].join(' ')
    }
  }
}
</script>
